const API_BASE_URL = 'https://universal-matching-game-production.up.railway.app';

export { API_BASE_URL };

export interface User {
  id: number;
  username: string;
  email: string;
  role: 'user' | 'admin' | 'teacher' | 'banned';
  created_at?: string;
  createdAt?: string;
}

export type MatchingMode = 'one-to-one' | 'category' | 'sort';

export interface DictionaryMetadata {
  matchingMode: MatchingMode;
  categories?: Record<string, string[]>; // For category mode
}

export interface SortDictionaryMetadata {
  matchingMode: 'sort';
}

export interface CategoryDictionaryData {
  items: Record<string, string>;
  metadata: DictionaryMetadata;
}

export interface SortDictionaryData {
  items: Record<string, string>; // item → label mapping
  labels: string[]; // Array of label names
  metadata: SortDictionaryMetadata;
}

export type DictionaryData = Record<string, string> | CategoryDictionaryData | SortDictionaryData;

export interface Dictionary {
  id: number;
  name: string;
  data: DictionaryData;
  matching_mode?: MatchingMode;
  is_public: boolean;
  created_at: string;
  description?: string;
  updated_at?: string;
}

export interface AuthResponse {
  success: boolean;
  token?: string;
  user?: User;
  message?: string;
  requiresVerification?: boolean;
  userId?: number;
  conflictType?: 'username' | 'email';
  existingUsername?: string;
  // Suspension fields
  suspended?: boolean;
  suspensionReason?: string;
  adminMessage?: string;
  error?: string;
}

class ApiService {
  private token: string | null = null;

  constructor() {
    // Load token from localStorage on initialization
    this.token = localStorage.getItem('authToken');
  }

  private async request(endpoint: string, options: RequestInit = {}): Promise<any> {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    };

    if (this.token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      
      // Handle authentication errors
      if (response.status === 401) {
        console.log('❌ Auth error, clearing token:', error.error);
        this.logout(); // Clear invalid token
        throw new Error(error.error || 'Authentication failed');
      }
      
      // Handle suspension - return the error object instead of throwing
      if (response.status === 403 && error.suspended) {
        return error; // Return the suspension data
      }
      
      // Handle rate limiting specifically
      if (response.status === 429) {
        throw new Error(error.error || 'Too many requests. Please wait before trying again.');
      }
      
      throw new Error(error.error || `Request failed: ${response.status}`);
    }

    return response.json();
  }

  // Helper methods for HTTP verbs
  async get(endpoint: string): Promise<any> {
    return this.request(endpoint, { method: 'GET' });
  }

  async post(endpoint: string, data?: any): Promise<any> {
    return this.request(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put(endpoint: string, data?: any): Promise<any> {
    return this.request(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete(endpoint: string): Promise<any> {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // Authentication methods
  async register(username: string, email: string, password: string): Promise<AuthResponse> {
    const response = await this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, email, password }),
    });
    
    if (response.token) {
      this.setToken(response.token);
    }
    
    return response;
  }

  async login(username: string, password: string): Promise<AuthResponse> {
    const response = await this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    
    if (response.token) {
      this.setToken(response.token);
    }
    
    return response;
  }

  async getProfile(): Promise<User> {
    const response = await this.request('/api/auth/profile');
    return response.user;
  }

  async getUserRoles(): Promise<{ isTeacher: boolean; isStudent: boolean; hasBothRoles: boolean }> {
    const response = await this.request('/api/auth/roles');
    return {
      isTeacher: response.isTeacher,
      isStudent: response.isStudent,
      hasBothRoles: response.hasBothRoles
    };
  }

  async verifyEmail(userId: number, code: string): Promise<AuthResponse> {
    const response = await this.request('/api/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ userId, code }),
    });
    
    // Don't auto-login after email verification
    // User should login manually for better security
    // if (response.token) {
    //   this.setToken(response.token);
    // }
    
    return response;
  }

  async resendVerification(userId: number): Promise<{ message: string }> {
    return this.request('/api/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  async forgotPassword(email: string): Promise<{ message: string; userId: number }> {
    return this.request('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(userId: number, code: string, newPassword: string): Promise<{ message: string }> {
    return this.request('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ userId, code, newPassword }),
    });
  }

  // Dictionary sharing methods
  async shareDictionary(dictionaryId: number, recipientUsername: string, message?: string): Promise<{ message: string; shareId: number }> {
    return this.request('/api/sharing/share-dictionary', {
      method: 'POST',
      body: JSON.stringify({ dictionaryId, recipientUsername, message }),
    });
  }

  async respondToShare(shareId: number, action: 'accept' | 'reject'): Promise<{ message: string; action: string; newDictionaryId?: number }> {
    return this.request('/api/sharing/respond-share', {
      method: 'POST',
      body: JSON.stringify({ shareId, action }),
    });
  }

  // Notification methods
  async getNotifications(): Promise<{ notifications: any[] }> {
    return this.request('/api/sharing/notifications');
  }

  async markNotificationRead(notificationId: number): Promise<{ message: string }> {
    return this.request(`/api/sharing/notifications/${notificationId}/read`, {
      method: 'POST',
    });
  }

  async getUnreadNotificationCount(): Promise<{ unreadCount: number }> {
    return this.request('/api/sharing/notifications/unread-count');
  }

  async acceptDictionaryShare(notificationId: number): Promise<{ message: string; action: string; newDictionaryId?: number }> {
    return this.respondToShare(notificationId, 'accept');
  }

  async rejectDictionaryShare(notificationId: number): Promise<{ message: string; action: string }> {
    return this.respondToShare(notificationId, 'reject');
  }

  logout(): void {
    this.token = null;
    localStorage.removeItem('authToken');
  }

  private setToken(token: string): void {
    this.token = token;
    localStorage.setItem('authToken', token);
  }

  isAuthenticated(): boolean {
    const hasToken = !!this.token;
    console.log('🔑 Token check:', hasToken ? 'Token exists' : 'No token');
    if (hasToken) {
      console.log('🔑 Token value:', this.token?.substring(0, 20) + '...');
    }
    return hasToken;
  }

  // Dictionary methods
  async getDictionaries(): Promise<Dictionary[]> {
    return this.request('/api/dictionaries');
  }

  // Dictionary Management (V2 API - Object-Oriented)
  async createDictionary(dictionaryData: {
    name: string;
    description?: string;
    content: Record<string, string>;
    category?: string;
    is_public?: boolean;
  }): Promise<any> {
    return this.request('/api/v2/dictionaries', {
      method: 'POST',
      body: JSON.stringify(dictionaryData),
    });
  }

  async getDictionary(dictionaryId: number): Promise<any> {
    return this.request(`/api/v2/dictionaries/${dictionaryId}`);
  }

  async updateDictionary(dictionaryId: number, updateData: any): Promise<any> {
    return this.request(`/api/v2/dictionaries/${dictionaryId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  }

  async deleteDictionaryV2(dictionaryId: number): Promise<any> {
    return this.request(`/api/v2/dictionaries/${dictionaryId}`, {
      method: 'DELETE',
    });
  }

  // Game Operations
  async recordGameSession(dictionaryId: number, gameResult: {
    correctMatches: number;
    wrongMatches: number;
    totalPairs: number;
  }): Promise<any> {
    return this.request(`/api/v2/dictionaries/${dictionaryId}/play`, {
      method: 'POST',
      body: JSON.stringify(gameResult),
    });
  }

  async getUserGameHistory(dictionaryId: number, limit: number = 50): Promise<any> {
    return this.request(`/api/v2/dictionaries/${dictionaryId}/history?limit=${limit}`);
  }

  async getDictionaryLeaderboard(dictionaryId: number, limit: number = 20): Promise<any> {
    return this.request(`/api/v2/dictionaries/${dictionaryId}/leaderboard?limit=${limit}`);
  }

  async getDictionaryAnalytics(dictionaryId: number): Promise<any> {
    return this.request(`/api/v2/dictionaries/${dictionaryId}/analytics`);
  }

  async getRandomPairs(dictionaryId: number, count: number): Promise<any> {
    return this.request(`/api/v2/dictionaries/${dictionaryId}/pairs?count=${count}`);
  }

  // Sharing Operations
  async shareDictionaryV2(dictionaryId: number, recipientUsername: string, message?: string): Promise<any> {
    return this.request(`/api/v2/dictionaries/${dictionaryId}/share`, {
      method: 'POST',
      body: JSON.stringify({ recipientUsername, message }),
    });
  }

  async searchPublicDictionaries(query?: string, category?: string, limit: number = 20): Promise<any> {
    const params = new URLSearchParams();
    if (query) params.append('q', query);
    if (category) params.append('category', category);
    params.append('limit', limit.toString());

    return this.request(`/api/v2/dictionaries/public/search?${params}`);
  }

  // Legacy Dictionary Management (V1 API - for backward compatibility)
  async saveDictionary(name: string, data: Record<string, string>, isPublic: boolean = false): Promise<Dictionary> {
    return this.request('/api/dictionaries', {
      method: 'POST',
      body: JSON.stringify({ name, data, is_public: isPublic }),
    });
  }

  async deleteDictionary(id: number): Promise<void> {
    await this.request(`/api/dictionaries/${id}`, {
      method: 'DELETE',
    });
  }

  async getPublicDictionaries(): Promise<Dictionary[]> {
    return this.request('/api/dictionaries/public');
  }

  // Legacy methods for backward compatibility
  async checkMatch(item1: string, item2: string): Promise<{ isMatch: boolean }> {
    return this.request('/api/check-match', {
      method: 'POST',
      body: JSON.stringify({ item1, item2 }),
    });
  }

  // Admin API methods
  async adminLogin(username: string, password: string): Promise<any> {
    return this.request('/api/admin/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  }

  async getAdminStats(): Promise<{ stats: any }> {
    return this.request('/api/admin/stats');
  }

  async getAdminUsers(page = 1, limit = 50, search = ''): Promise<{ users: any[], pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      search
    });
    return this.request(`/api/admin/users?${params}`);
  }

  async getAdminUserDetails(userId: number): Promise<{ user: any, dictionaries: any[], sharesSent: any[], sharesReceived: any[] }> {
    return this.request(`/api/admin/users/${userId}`);
  }

  async suspendUser(userId: number, reason: string): Promise<{ success: boolean, message: string, user: any }> {
    return this.request(`/api/admin/users/${userId}/suspend`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async unsuspendUser(userId: number): Promise<{ success: boolean, message: string, user: any }> {
    return this.request(`/api/admin/users/${userId}/unsuspend`, {
      method: 'POST',
    });
  }

  async getAdminInbox(): Promise<{ messages: any[] }> {
    return this.request('/api/admin/inbox');
  }

  async sendAdminMessage(userId: number, message: string, parentMessageId?: number): Promise<{ success: boolean, message: string, messageData: any }> {
    return this.request(`/api/admin/users/${userId}/message`, {
      method: 'POST',
      body: JSON.stringify({ message, parentMessageId }),
    });
  }

  async markAdminMessageRead(messageId: number): Promise<{ success: boolean, message: string }> {
    return this.request(`/api/admin/messages/${messageId}/read`, {
      method: 'POST',
    });
  }
  
  async deleteAdminConversation(userId: number): Promise<{ success: boolean, message: string }> {
    return this.request(`/api/admin/users/${userId}/conversation`, {
      method: 'DELETE',
    });
  }

  async getAllUsersDictionaries(): Promise<{ dictionaries: any[] }> {
    return this.request('/api/admin/all-dictionaries');
  }
  
  async deleteNotification(notificationId: number): Promise<{ success: boolean, message: string }> {
    return this.request(`/api/sharing/notifications/${notificationId}`, {
      method: 'DELETE',
    });
  }

  // Suspended user API methods
  async checkSuspensionStatus(): Promise<{ isSuspended: boolean, suspensionData?: any }> {
    return this.request('/api/auth/suspension/status');
  }

  async sendSuspensionMessage(message: string): Promise<{ success: boolean, message: string, canSendMore: boolean, messagesLeft: number }> {
    return this.request('/api/auth/suspension/messages', {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  }

  async getSuspensionMessages(): Promise<{ messages: any[], canSendMore: boolean, messagesLeft: number }> {
    return this.request('/api/auth/suspension/messages');
  }

  // AI Chat Methods
  async getAIModels(): Promise<{ models: any[] }> {
    return this.request('/api/ai/models');
  }

  async createAIChatSession(title: string = 'New AI Dictionary Chat'): Promise<{ session: any, welcomeMessage: string }> {
    return this.request('/api/ai/sessions', {
      method: 'POST',
      body: JSON.stringify({ title }),
    });
  }

  async getAIChatSessions(page: number = 1, limit: number = 20): Promise<{ sessions: any[], pagination: any }> {
    return this.request(`/api/ai/sessions?page=${page}&limit=${limit}`);
  }

  async getAIChatSession(sessionId: number): Promise<{ session: any, messages: any[] }> {
    return this.request(`/api/ai/sessions/${sessionId}`);
  }

  async sendAIMessage(sessionId: number, content: string, url?: string): Promise<{ message: string, usage?: any, dictionary?: any, remainingMessages: number, session?: any }> {
    return this.request(`/api/ai/sessions/${sessionId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content, url }),
    });
  }

  async updateAIChatSession(sessionId: number, updates: { title?: string }): Promise<{ session: any }> {
    return this.request(`/api/ai/sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteAIChatSession(sessionId: number): Promise<{ message: string }> {
    return this.request(`/api/ai/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  }

  async getAIUsageStats(): Promise<{ dailyLimits: any, remaining: any, canCreateChat: boolean, canSendMessage: boolean }> {
    return this.request('/api/ai/usage');
  }

  // Admin: Promote user to teacher
  async promoteUserToTeacher(userId: number): Promise<{ success: boolean, message: string }> {
    return this.request(`/api/admin/users/${userId}/promote-teacher`, {
      method: 'POST',
    });
  }

  // Admin: Demote teacher to user
  async demoteTeacherToUser(userId: number): Promise<{ success: boolean, message: string }> {
    return this.request(`/api/admin/users/${userId}/demote-teacher`, {
      method: 'POST',
    });
  }

  // Teacher: Get students
  async getTeacherStudents(): Promise<any> {
    return this.request('/api/teacher/students');
  }

  // Teacher: Get available students
  async getAvailableStudents(): Promise<any> {
    return this.request('/api/teacher/available-students');
  }

  // Teacher: Add students
  async addStudents(studentIds: number[], notes?: string): Promise<any> {
    return this.request('/api/teacher/students', {
      method: 'POST',
      body: JSON.stringify({ studentIds, notes }),
    });
  }

  // Teacher: Remove student
  async removeStudent(studentId: number): Promise<any> {
    return this.request(`/api/teacher/students/${studentId}`, {
      method: 'DELETE',
    });
  }

  // Teacher: Get student progress
  async getStudentProgress(studentId: number): Promise<any> {
    return this.request(`/api/teacher/students/${studentId}/progress`);
  }

  // Teacher: Get analytics
  async getTeacherAnalytics(): Promise<any> {
    return this.request('/api/teacher/analytics');
  }

  // ============================================================================
  // ASSIGNMENT ENDPOINTS - Teacher
  // ============================================================================

  // Classes
  async getTeacherClasses(): Promise<any> {
    return this.request('/api/assignments/classes');
  }

  async createClass(name: string, description?: string): Promise<any> {
    return this.request('/api/assignments/classes', {
      method: 'POST',
      body: JSON.stringify({ name, description }),
    });
  }

  async updateClass(classId: number, name: string, description?: string): Promise<any> {
    return this.request(`/api/assignments/classes/${classId}`, {
      method: 'PUT',
      body: JSON.stringify({ name, description }),
    });
  }

  async deleteClass(classId: number): Promise<any> {
    return this.request(`/api/assignments/classes/${classId}`, {
      method: 'DELETE',
    });
  }

  async getClassStudents(classId: number): Promise<any> {
    return this.request(`/api/assignments/classes/${classId}/students`);
  }

  async addStudentsToClass(classId: number, studentIds: number[]): Promise<any> {
    return this.request(`/api/assignments/classes/${classId}/students`, {
      method: 'POST',
      body: JSON.stringify({ studentIds }),
    });
  }

  async removeStudentFromClass(classId: number, studentId: number): Promise<any> {
    return this.request(`/api/assignments/classes/${classId}/students/${studentId}`, {
      method: 'DELETE',
    });
  }

  // Assignments
  async getTeacherAssignments(): Promise<any> {
    return this.request('/api/assignments');
  }

  async getAssignmentDetails(assignmentId: number): Promise<any> {
    return this.request(`/api/assignments/${assignmentId}`);
  }

  async createAssignment(data: {
    classId: number;
    name: string;
    description?: string;
    requirements?: any;
    dueDate?: string;
    games: Array<{ dictionaryId?: number; builtInGameId?: string }>;
  }): Promise<any> {
    return this.request('/api/assignments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAssignment(assignmentId: number, data: {
    name: string;
    description?: string;
    requirements?: any;
    dueDate?: string;
    games?: Array<{ dictionaryId?: number; builtInGameId?: string }>;
  }): Promise<any> {
    return this.request(`/api/assignments/${assignmentId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAssignment(assignmentId: number): Promise<any> {
    return this.request(`/api/assignments/${assignmentId}`, {
      method: 'DELETE',
    });
  }

  // Progress tracking
  async getProgressOverview(): Promise<any> {
    return this.request('/api/assignments/progress/overview');
  }

  async getAssignmentProgress(assignmentId: number): Promise<any> {
    return this.request(`/api/assignments/${assignmentId}/progress`);
  }

  // ============================================================================
  // ASSIGNMENT ENDPOINTS - Student
  // ============================================================================

  async getMyAssignments(): Promise<any> {
    return this.request('/api/assignments/student/my-assignments');
  }

  async getAssignmentDetailsForStudent(assignmentId: number): Promise<any> {
    return this.request(`/api/assignments/student/${assignmentId}/details`);
  }

  async recordAssignmentAttempt(assignmentId: number, data: {
    gameId: number;
    score: number;
    accuracy?: number;
    correctMatches?: number;
    wrongMatches?: number;
    timeSpent?: number;
  }): Promise<any> {
    return this.request(`/api/assignments/student/${assignmentId}/attempt`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

export const apiService = new ApiService();